/**
 * Text processing utilities for StudioAI feature
 */

import { ChartSpec } from '@/@studio-ai/types';
import { tracking } from '@/lib/features/tracking';
import { WORD_REPLACEMENTS } from '../constants';

/**
 * Determine the case pattern of a word
 */
function getCasePattern(word: string): 'lower' | 'upper' | 'title' | 'mixed' {
    if (word === word.toLowerCase()) return 'lower';
    if (word === word.toUpperCase()) return 'upper';
    if (word[0] === word[0].toUpperCase() && word.slice(1) === word.slice(1).toLowerCase()) {
        return 'title';
    }
    return 'mixed';
}

/**
 * Apply case pattern to a replacement word
 */
function applyCasePattern(word: string, pattern: 'lower' | 'upper' | 'title' | 'mixed'): string {
    switch (pattern) {
        case 'lower':
            return word.toLowerCase();
        case 'upper':
            return word.toUpperCase();
        case 'title':
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        case 'mixed':
        default:
            return word;
    }
}

/**
 * Escape special regex characters in a string
 */
function escapeRegex(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Apply word replacements to text
 * Replaces specific words while preserving case patterns and handling plurals
 * Works with compound words (e.g., PANELIST_COUNT -> CONSUMER_COUNT)
 */
export function applyWordReplacements(text: string): string {
    let result = text;

    // Apply each replacement
    for (const [original, replacement] of WORD_REPLACEMENTS) {
        // Create regex that matches the word with optional 's' for plural
        // Use case-insensitive flag and capture groups to preserve case
        const regex = new RegExp(`${escapeRegex(original)}(s?)`, 'gi');

        result = result.replace(regex, (match, pluralS) => {
            // Determine the case pattern of the matched word (excluding the plural 's')
            const baseMatch = pluralS.length > 0 ? match.slice(0, -pluralS.length) : match;
            const casePattern = getCasePattern(baseMatch);

            // Apply the same case pattern to the replacement
            let replacedWord = applyCasePattern(replacement, casePattern);

            // Add plural 's' back if it was present, preserving its case
            if (pluralS) {
                if (pluralS === 's') {
                    replacedWord += 's';
                } else if (pluralS === 'S') {
                    replacedWord += 'S';
                }
            }

            return replacedWord;
        });
    }

    return result;
}

/**
 * Compare two semantic version strings (e.g. "1.2.3") numerically, segment by segment.
 * Returns a negative number if a < b, positive if a > b, 0 if equal.
 */
export function compareSemanticVersions(a: string, b: string): number {
    const pa = a.split('.').map(Number);
    const pb = b.split('.').map(Number);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const diff = (pa[i] || 0) - (pb[i] || 0);
        if (diff !== 0) return diff;
    }
    return 0;
}

/**
 * Render a machine-style token (snake_case, kebab-case, camelCase) as a
 * human-readable label with spaces and capitalized words. Nullish/empty
 * values return an empty string; arrays are joined with ', '. Anything that
 * is not a string or array (numbers, booleans) is stringified verbatim.
 */
export function humanizeLabel(
    value: string | number | boolean | null | undefined | Array<string | number | boolean>,
): string {
    if (value == null) return '';
    if (Array.isArray(value)) {
        return value
            .map((entry) => humanizeLabel(entry))
            .filter((entry) => entry.length > 0)
            .join(', ');
    }
    if (typeof value !== 'string') {
        return String(value);
    }
    const spaced = value
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim();
    if (!spaced) return '';
    return spaced
        .split(' ')
        .map((word) => {
            if (word.length <= 3 && word === word.toUpperCase()) {
                return word;
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
}

/**
 * Apply word replacements to chart specification
 * Parses the chart_spec JSON, applies replacements, and returns the updated JSON string
 */
export function applyReplacementsToChartSpec(chartSpec: string): string {
    try {
        // Parse the chart spec JSON
        const spec = JSON.parse(chartSpec);

        // Apply replacements to all string values in the spec recursively
        const applyToObject = (obj: ChartSpec): ChartSpec => {
            if (obj?.title) {
                obj.title = applyWordReplacements(obj.title);
            }
            if (obj?.encoding?.x?.title) {
                obj.encoding.x.title = applyWordReplacements(obj.encoding.x.title);
            }
            if (obj?.encoding?.y?.title) {
                obj.encoding.y.title = applyWordReplacements(obj.encoding.y.title);
            }
            return obj;
        };

        const updatedSpec = applyToObject(spec);
        return JSON.stringify(updatedSpec);
    } catch (error) {
        // If parsing fails, return the original spec
        tracking.errorWithSource(error as Error, 'applyReplacementsToChartSpec', { chartSpec });
        return chartSpec;
    }
}
